import {
  Color3,
  Matrix,
  Mesh,
  MeshBuilder,
  Quaternion,
  Scene,
  StandardMaterial,
  Texture,
  DynamicTexture,
  Vector3,
} from '@babylonjs/core';
import type { TreeInstance } from '@roadworld/world';
import { PALETTE } from '../engine/palettes.js';

/** Visual tree geometry constants — exported so the "falling tree" mesh built when a tree
 *  is felled can match the standing tree exactly. */
export const TREE_TRUNK_HEIGHT = 1.6;
export const TREE_CROWN_HEIGHT = 4.0;
export const TREE_TRUNK_DIAMETER = 0.35;
export const TREE_CROWN_BASE_DIAMETER = 2.6;
/** Total visual height at scale=1 (trunk + crown). */
export const TREE_TOTAL_HEIGHT = TREE_TRUNK_HEIGHT + TREE_CROWN_HEIGHT;

/**
 * Per-chunk tree batch. Trunks and crowns are thin-instanced base meshes; the matrices
 * are kept so individual trees can be hidden when felled (zero scale collapses them to
 * a point). Owns the base meshes — call dispose() on chunk unload.
 */
export interface TreeBatch {
  trees: readonly TreeInstance[];
  /** World position of each tree's base (chunk center + local). Convenience for callers. */
  worldX: Float32Array;
  worldZ: Float32Array;
  /** Base meshes (trunk + crown) — exposed so callers can register them with a shadow
   *  generator. Thin-instanced; registering with `includeInstances=true` casts all trees. */
  baseMeshes: Mesh[];
  /** Hide tree `i` in the batch. Idempotent; cheap. Does NOT remove physics colliders. */
  hide(i: number): void;
  isHidden(i: number): boolean;
  /** Toggle rendering of the entire batch (used by Chunk.setLOD to swap between cylinder
   *  trees and billboard trees). Does not affect hide() state or physics colliders. */
  setEnabled(on: boolean): void;
  /** Refresh per-instance camera-facing rotation. No-op for the cylinder batch; the
   *  billboard batch uses this to point each quad at the camera. Called once per frame
   *  by the render loop with the chase camera's world XZ. */
  updateBillboard(cameraX: number, cameraZ: number): void;
  dispose(): void;
}

export function buildTreeBatch(
  scene: Scene,
  key: string,
  trees: TreeInstance[],
  rootOffset: Vector3,
): TreeBatch | null {
  if (trees.length === 0) return null;

  const trunk = MeshBuilder.CreateCylinder(
    `trunk-base-${key}`,
    { diameter: TREE_TRUNK_DIAMETER, height: TREE_TRUNK_HEIGHT, tessellation: 6 },
    scene,
  );
  const trunkMat = new StandardMaterial(`trunk-mat-${key}`, scene);
  trunkMat.diffuseColor = PALETTE.trunk.clone();
  trunkMat.specularColor = new Color3(0.02, 0.02, 0.02);
  trunk.material = trunkMat;

  const crown = MeshBuilder.CreateCylinder(
    `crown-base-${key}`,
    {
      diameterTop: 0,
      diameterBottom: TREE_CROWN_BASE_DIAMETER,
      height: TREE_CROWN_HEIGHT,
      tessellation: 8,
    },
    scene,
  );
  const crownMat = new StandardMaterial(`crown-mat-${key}`, scene);
  crownMat.diffuseColor = PALETTE.crown.clone();
  crownMat.specularColor = new Color3(0.02, 0.02, 0.02);
  crown.material = crownMat;

  const trunkMatrices = new Float32Array(trees.length * 16);
  const crownMatrices = new Float32Array(trees.length * 16);
  const worldX = new Float32Array(trees.length);
  const worldZ = new Float32Array(trees.length);
  const hidden = new Uint8Array(trees.length);
  const tmpQ = new Quaternion();
  const tmpM = new Matrix();
  const trunkOffsetUnit = new Vector3(0, TREE_TRUNK_HEIGHT * 0.5, 0);
  const crownOffsetUnit = new Vector3(0, TREE_TRUNK_HEIGHT + TREE_CROWN_HEIGHT * 0.5, 0);

  for (let i = 0; i < trees.length; i++) {
    const t = trees[i]!;
    Quaternion.RotationAxisToRef(Vector3.UpReadOnly, t.yaw, tmpQ);
    const wx = t.x + rootOffset.x;
    const wz = t.z + rootOffset.z;
    worldX[i] = wx;
    worldZ[i] = wz;

    const tPos = new Vector3(wx, t.y, wz).add(trunkOffsetUnit.scale(t.scale));
    Matrix.ComposeToRef(new Vector3(t.scale, t.scale, t.scale), tmpQ, tPos, tmpM);
    tmpM.copyToArray(trunkMatrices, i * 16);

    const cPos = new Vector3(wx, t.y, wz).add(crownOffsetUnit.scale(t.scale));
    Matrix.ComposeToRef(new Vector3(t.scale, t.scale, t.scale), tmpQ, cPos, tmpM);
    tmpM.copyToArray(crownMatrices, i * 16);
  }

  trunk.thinInstanceSetBuffer('matrix', trunkMatrices, 16, false);
  crown.thinInstanceSetBuffer('matrix', crownMatrices, 16, false);
  trunk.alwaysSelectAsActiveMesh = true;
  crown.alwaysSelectAsActiveMesh = true;

  // Reusable zeroed matrix for hide() — a zero-scale matrix collapses the instance to a
  // point, which is the cheapest way to remove it without resizing the buffer.
  const zero = new Float32Array(16);

  const hide = (i: number): void => {
    if (i < 0 || i >= trees.length || hidden[i]) return;
    hidden[i] = 1;
    trunkMatrices.set(zero, i * 16);
    crownMatrices.set(zero, i * 16);
    trunk.thinInstanceBufferUpdated('matrix');
    crown.thinInstanceBufferUpdated('matrix');
  };

  const isHidden = (i: number): boolean => i >= 0 && i < trees.length && hidden[i] === 1;

  const setEnabled = (on: boolean): void => {
    trunk.setEnabled(on);
    crown.setEnabled(on);
  };

  const dispose = (): void => {
    trunk.dispose(false, true);
    crown.dispose(false, true);
  };

  // Cylinder trees are world-positioned with a static yaw — no per-frame work needed.
  const updateBillboard = (_cameraX: number, _cameraZ: number): void => {
    void _cameraX;
    void _cameraZ;
  };

  return {
    trees,
    worldX,
    worldZ,
    baseMeshes: [trunk, crown],
    hide,
    isHidden,
    setEnabled,
    updateBillboard,
    dispose,
  };
}

/**
 * Cheap LOD variant. Each tree is a single quad textured with a flat trunk-and-crown
 * silhouette, rotated to face the camera. Built once per chunk; the shared material +
 * texture are cached on the scene via the helper below.
 *
 * Camera-facing rotation is baked into each thin-instance matrix by `updateBillboard`,
 * called once per frame from the render loop. We deliberately do NOT use Babylon's
 * `BILLBOARDMODE_Y` here — billboard mode rewrites the *base* mesh's world matrix each
 * frame, and Babylon's thin-instance shader does `finalWorld = baseMeshWorld * instanceM`.
 * Because each instance matrix carries a world-space translation, billboard rotation
 * rotates every instance's position around the world origin, sending distant trees flying
 * across the sky as the camera yaws.
 *
 * Same `TreeBatch` interface as `buildTreeBatch` so Chunk can swap between the two
 * without conditional rendering paths.
 */
export function buildTreeBillboardBatch(
  scene: Scene,
  key: string,
  trees: TreeInstance[],
  rootOffset: Vector3,
): TreeBatch | null {
  if (trees.length === 0) return null;

  // Quad sized so a unit-scale tree matches the cylinder+cone variant's silhouette.
  // Width = crown base diameter (so the canopy reads), height = full tree height.
  // Single-sided geometry; the material has `backFaceCulling = false` so we still see
  // both sides — saves the extra triangles `Mesh.DOUBLESIDE` would emit.
  const quad = MeshBuilder.CreatePlane(
    `tree-bb-${key}`,
    { width: TREE_CROWN_BASE_DIAMETER, height: TREE_TOTAL_HEIGHT },
    scene,
  );
  quad.material = getTreeBillboardMaterial(scene);
  quad.alwaysSelectAsActiveMesh = true;

  const matrices = new Float32Array(trees.length * 16);
  const worldX = new Float32Array(trees.length);
  const worldZ = new Float32Array(trees.length);
  // Per-instance Y (lifted) and scale, needed each frame by updateBillboard to recompose
  // the matrix with a fresh camera-facing yaw.
  const worldY = new Float32Array(trees.length);
  const scales = new Float32Array(trees.length);
  const hidden = new Uint8Array(trees.length);
  const tmpQ = new Quaternion();
  const tmpM = new Matrix();
  const tmpS = new Vector3();
  const tmpPos = new Vector3();
  // Quads are centered on their origin, so lift each instance by half its total height to
  // plant the base at the terrain sample point.
  const lift = TREE_TOTAL_HEIGHT * 0.5;

  for (let i = 0; i < trees.length; i++) {
    const t = trees[i]!;
    const wx = t.x + rootOffset.x;
    const wz = t.z + rootOffset.z;
    worldX[i] = wx;
    worldZ[i] = wz;
    worldY[i] = t.y + lift * t.scale;
    scales[i] = t.scale;
    // Initial pose: identity rotation. updateBillboard rewrites this on the first frame
    // anyway, but having a valid matrix means the batch renders correctly the very first
    // frame before the render loop has had a chance to call updateBillboard.
    Quaternion.RotationAxisToRef(Vector3.UpReadOnly, 0, tmpQ);
    tmpS.set(t.scale, t.scale, t.scale);
    tmpPos.set(wx, worldY[i]!, wz);
    Matrix.ComposeToRef(tmpS, tmpQ, tmpPos, tmpM);
    tmpM.copyToArray(matrices, i * 16);
  }

  quad.thinInstanceSetBuffer('matrix', matrices, 16, false);

  const zero = new Float32Array(16);
  const hide = (i: number): void => {
    if (i < 0 || i >= trees.length || hidden[i]) return;
    hidden[i] = 1;
    matrices.set(zero, i * 16);
    quad.thinInstanceBufferUpdated('matrix');
  };
  const isHidden = (i: number): boolean => i >= 0 && i < trees.length && hidden[i] === 1;
  const setEnabled = (on: boolean): void => {
    quad.setEnabled(on);
  };

  // Rebuild matrices each frame so every quad faces the camera. Skipped when the camera
  // has barely moved (< 0.25 m XZ); this catches the common "idle" and "straight-line at
  // 30 m/s for one frame" cases without producing a visible swim on the quads.
  let lastCamX = Number.POSITIVE_INFINITY;
  let lastCamZ = Number.POSITIVE_INFINITY;
  const updateBillboard = (cameraX: number, cameraZ: number): void => {
    // Skip when the batch isn't the active LOD — billboards only render in cached-ring
    // chunks; high-LOD chunks have this quad disabled and don't need a fresh rotation.
    if (!quad.isEnabled()) return;
    const dx = cameraX - lastCamX;
    const dz = cameraZ - lastCamZ;
    if (dx * dx + dz * dz < 0.0625) return; // 0.25 m threshold
    lastCamX = cameraX;
    lastCamZ = cameraZ;

    for (let i = 0; i < trees.length; i++) {
      if (hidden[i]) continue;
      const wx = worldX[i]!;
      const wz = worldZ[i]!;
      // atan2(x, z) gives the yaw (around +Y) needed for the quad's local +Z to point
      // toward the camera. The quad's geometry faces local +Z, so this aligns its front
      // with the camera horizontally — exactly what BILLBOARDMODE_Y would do, but
      // computed per-instance instead of on the base mesh.
      const yaw = Math.atan2(cameraX - wx, cameraZ - wz);
      Quaternion.RotationAxisToRef(Vector3.UpReadOnly, yaw, tmpQ);
      const s = scales[i]!;
      tmpS.set(s, s, s);
      tmpPos.set(wx, worldY[i]!, wz);
      Matrix.ComposeToRef(tmpS, tmpQ, tmpPos, tmpM);
      tmpM.copyToArray(matrices, i * 16);
    }
    quad.thinInstanceBufferUpdated('matrix');
  };

  // Don't dispose the shared material/texture — they're cached on the scene and reused
  // by every billboard batch. Just drop the per-chunk mesh.
  const dispose = (): void => {
    quad.dispose(false, false);
  };

  // Billboard quads are not registered as shadow casters — a flat alpha-tested plane
  // projects a rectangular silhouette which looks wrong on the ground. Leave baseMeshes
  // empty so consumers know there's nothing to shadow-cast at this LOD.
  return {
    trees,
    worldX,
    worldZ,
    baseMeshes: [],
    hide,
    isHidden,
    setEnabled,
    updateBillboard,
    dispose,
  };
}

/** Scene-scoped cache of the shared billboard material. Computing the painted texture is
 *  cheap but doing it 50× per session is wasted; storing it on the scene keeps it alive
 *  across chunk swaps and naturally gets cleaned up with the scene. */
function getTreeBillboardMaterial(scene: Scene): StandardMaterial {
  const cacheKey = '__treeBillboardMaterial';
  const sceneAny = scene as Scene & { [cacheKey]?: StandardMaterial };
  const cached = sceneAny[cacheKey];
  if (cached) return cached;

  const tex = new DynamicTexture('tree-bb-tex', { width: 64, height: 256 }, scene, false);
  tex.hasAlpha = true;
  const ctx = tex.getContext();
  ctx.clearRect(0, 0, 64, 256);
  // Trunk — vertical brown stripe in the lower 25% of the texture.
  ctx.fillStyle = '#52371d';
  ctx.fillRect(28, 200, 8, 56);
  // Crown — green teardrop/triangle filling the upper 75%.
  ctx.beginPath();
  ctx.moveTo(32, 0);
  ctx.lineTo(60, 200);
  ctx.lineTo(4, 200);
  ctx.closePath();
  ctx.fillStyle = '#2e6b32';
  ctx.fill();
  tex.update(false);

  const mat = new StandardMaterial('tree-bb-mat-shared', scene);
  mat.diffuseTexture = tex;
  mat.useAlphaFromDiffuseTexture = true;
  // Avoid the half-transparent edge pixels causing depth-sort fights — alpha-test instead.
  mat.diffuseTexture.hasAlpha = true;
  (mat.diffuseTexture as Texture).updateSamplingMode?.(Texture.NEAREST_SAMPLINGMODE);
  mat.specularColor = new Color3(0, 0, 0);
  mat.backFaceCulling = false;
  // Alpha test cutoff — pixels with alpha below the threshold are discarded entirely,
  // avoiding blending and the associated sort issues for distant trees.
  mat.transparencyMode = 1; // ALPHATEST
  mat.alphaCutOff = 0.5;

  sceneAny[cacheKey] = mat;
  return mat;
}
